import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../App";
import BackOfficeLayout from "./BackOfficeLayout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Separator } from "../components/ui/separator";
import { Progress } from "../components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  FileText,
  User,
  Building2,
  DollarSign,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  ArrowUpRight,
  ArrowLeft,
  Loader2,
  AlertTriangle,
  MessageSquare,
  History,
  Shield,
  Mail,
  Send,
  Briefcase,
  MapPin,
  Phone,
  Globe,
  Users,
  TrendingUp,
  FileCheck,
  Download,
  Eye,
  BarChart3,
  RefreshCw,
  Brain
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BackOfficeReview = () => {
  const { applicationId } = useParams();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [creditScore, setCreditScore] = useState(null);
  const [alternativeData, setAlternativeData] = useState([]);
  const [scoreLoading, setScoreLoading] = useState(false);
  
  const [recommendation, setRecommendation] = useState("");
  const [riskNotes, setRiskNotes] = useState("");
  const [managerComment, setManagerComment] = useState("");
  const [executiveComment, setExecutiveComment] = useState("");
  
  const [showRecommendDialog, setShowRecommendDialog] = useState(false);
  const [showManagerDialog, setShowManagerDialog] = useState(false);
  const [showExecutiveDialog, setShowExecutiveDialog] = useState(false);
  const [pendingDecision, setPendingDecision] = useState("");

  useEffect(() => {
    fetchApplication();
    fetchNotifications();
    fetchDocuments();
    fetchAlternativeData();
    fetchCreditScore();
  }, [applicationId]);

  const fetchApplication = async () => {
    try {
      const response = await axios.get(`${API}/backoffice/application/${applicationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApplication(response.data);
    } catch (error) {
      console.error("Failed to fetch application:", error);
      toast.error("Failed to load application");
      navigate("/backoffice/applications");
    } finally {
      setLoading(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/backoffice/notifications/${applicationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data.notifications || []);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    }
  };

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${API}/documents/${applicationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data || []);
    } catch (error) {
      console.error("Failed to fetch documents:", error);
    }
  };

  const fetchAlternativeData = async () => {
    try {
      const response = await axios.get(`${API}/alternative-data/${applicationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAlternativeData(response.data || []);
    } catch (error) {
      console.error("Failed to fetch alternative data:", error);
    }
  };

  const fetchCreditScore = async () => {
    try {
      const response = await axios.get(`${API}/credit-score/${applicationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCreditScore(response.data);
    } catch (error) {
      console.error("Failed to fetch credit score:", error);
    }
  };

  const calculateScore = async () => {
    setScoreLoading(true);
    try {
      const response = await axios.post(`${API}/credit-score/calculate/${applicationId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCreditScore(response.data);
      toast.success("Credit score calculated successfully");
    } catch (error) {
      console.error("Failed to calculate credit score:", error);
      toast.error(error.response?.data?.detail || "Failed to calculate credit score");
    } finally {
      setScoreLoading(false);
    }
  };

  const downloadDocument = async (documentId) => {
    try {
      const response = await axios.get(
        `${API}/documents/${applicationId}/${documentId}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Get filename from content-disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'document';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match) filename = match[1];
      }
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Document downloaded successfully");
    } catch (error) {
      console.error("Failed to download document:", error);
      toast.error(error.response?.data?.detail || "Failed to download document");
    }
  };

  const runAIAssessment = async () => {
    setScoreLoading(true);
    try {
      const response = await axios.post(`${API}/loans/${applicationId}/assess-risk`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Refresh application data to get AI assessment
      await fetchApplication();
      await fetchCreditScore();
      toast.success("AI Risk Assessment completed");
    } catch (error) {
      console.error("Failed to run AI assessment:", error);
      toast.error(error.response?.data?.detail || "Failed to run AI assessment");
    } finally {
      setScoreLoading(false);
    }
  };

  const handleRecommendation = async () => {
    if (!recommendation.trim()) {
      toast.error("Please enter a recommendation");
      return;
    }
    
    setActionLoading(true);
    try {
      await axios.post(`${API}/backoffice/recommend`, {
        application_id: applicationId,
        recommendation,
        risk_notes: riskNotes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Recommendation submitted successfully");
      setShowRecommendDialog(false);
      fetchApplication();
    } catch (error) {
      console.error("Failed to submit recommendation:", error);
      toast.error(error.response?.data?.detail || "Failed to submit recommendation");
    } finally {
      setActionLoading(false);
    }
  };

  const handleManagerDecision = async (decision) => {
    if (!managerComment.trim()) {
      toast.error("Please enter a comment");
      return;
    }
    
    setActionLoading(true);
    try {
      await axios.post(`${API}/backoffice/manager-decision`, {
        application_id: applicationId,
        decision,
        comment: managerComment
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Application ${decision}`);
      setShowManagerDialog(false);
      fetchApplication();
    } catch (error) {
      console.error("Failed to submit decision:", error);
      toast.error(error.response?.data?.detail || "Failed to submit decision");
    } finally {
      setActionLoading(false);
    }
  };

  const handleExecutiveDecision = async (decision) => {
    if (!executiveComment.trim()) {
      toast.error("Please enter a comment");
      return;
    }
    
    setActionLoading(true);
    try {
      await axios.post(`${API}/backoffice/executive-decision`, {
        application_id: applicationId,
        decision,
        comment: executiveComment
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Application ${decision}`);
      setShowExecutiveDialog(false);
      fetchApplication();
    } catch (error) {
      console.error("Failed to submit decision:", error);
      toast.error(error.response?.data?.detail || "Failed to submit decision");
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const configs = {
      pending: { color: "bg-yellow-100 text-yellow-700", icon: Clock, label: "Pending" },
      under_review: { color: "bg-blue-100 text-blue-700", icon: FileText, label: "Under Review" },
      recommended: { color: "bg-indigo-100 text-indigo-700", icon: FileText, label: "Recommended" },
      escalated: { color: "bg-orange-100 text-orange-700", icon: ArrowUpRight, label: "Escalated" },
      approved: { color: "bg-green-100 text-green-700", icon: CheckCircle, label: "Approved" },
      rejected: { color: "bg-red-100 text-red-700", icon: XCircle, label: "Rejected" }
    };
    
    const config = configs[status] || { color: "bg-gray-100 text-gray-700", icon: Clock, label: status };
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.color} flex items-center gap-1 text-sm px-3 py-1`}>
        <Icon className="h-4 w-4" />
        {config.label}
      </Badge>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getApplicantName = () => {
    return application?.business_name || application?.institution_name || 
           application?.full_name || application?.user_name || "N/A";
  };

  const getRiskColor = (score) => {
    if (score >= 70) return "text-green-600";
    if (score >= 50) return "text-yellow-600";
    return "text-red-600";
  };

  const getRiskBgColor = (score) => {
    if (score >= 70) return "bg-green-100";
    if (score >= 50) return "bg-yellow-100";
    return "bg-red-100";
  };

  const amount = application?.amount || application?.amount_required || 0;
  const canRecommend = user?.role === "loan_officer" && 
    (application?.status === "pending" || application?.status === "under_review");
  const canManagerDecide = (user?.role === "manager" || user?.role === "admin") && 
    application?.status === "recommended";
  const canExecutiveDecide = (user?.role === "executive" || user?.role === "admin") && 
    application?.status === "escalated";

  // Render a detail field if it has a value
  const DetailField = ({ label, value, icon: Icon }) => {
    if (!value && value !== 0) return null;
    return (
      <div className="py-2">
        <p className="text-xs text-gray-500 uppercase tracking-wide flex items-center gap-1">
          {Icon && <Icon className="h-3 w-3" />}
          {label}
        </p>
        <p className="font-medium text-gray-900 mt-0.5">{value}</p>
      </div>
    );
  };

  if (loading) {
    return (
      <BackOfficeLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </BackOfficeLayout>
    );
  }

  if (!application) {
    return (
      <BackOfficeLayout>
        <div className="text-center py-20">
          <p className="text-gray-500">Application not found</p>
        </div>
      </BackOfficeLayout>
    );
  }


  return (
    <BackOfficeLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <Button
              variant="ghost"
              size="sm"
              className="mb-2"
              onClick={() => navigate("/backoffice/applications")}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Applications
            </Button>
            <h1 className="text-2xl font-bold text-gray-900">Application Review</h1>
            <p className="text-gray-600 font-mono text-sm">{applicationId}</p>
          </div>
          <div className="flex items-center gap-3">
            {getStatusBadge(application.status)}
            <span className="text-2xl font-bold text-green-700">
              {formatCurrency(amount)}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Tabbed Content */}
            <Tabs defaultValue="details" className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="details" data-testid="tab-details">
                  <FileText className="h-4 w-4 mr-2" />
                  Details
                </TabsTrigger>
                <TabsTrigger value="financials" data-testid="tab-financials">
                  <DollarSign className="h-4 w-4 mr-2" />
                  Financials
                </TabsTrigger>
                <TabsTrigger value="documents" data-testid="tab-documents">
                  <FileCheck className="h-4 w-4 mr-2" />
                  Documents
                  {(documents.length > 0 || application.documents_count > 0) && (
                    <Badge className="ml-2 bg-blue-100 text-blue-700 text-xs">
                      {documents.length || application.documents_count || 0}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="history" data-testid="tab-history">
                  <History className="h-4 w-4 mr-2" />
                  History
                </TabsTrigger>
              </TabsList>

              {/* Details Tab */}
              <TabsContent value="details" className="space-y-4 mt-4">
                {/* Basic Applicant Info */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      {application.applicant_type === "individual" ? (
                        <User className="h-5 w-5" />
                      ) : (
                        <Building2 className="h-5 w-5" />
                      )}
                      {application.applicant_type === "individual" ? "Personal Information" : "Business Information"}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <DetailField label="Name" value={getApplicantName()} icon={User} />
                      <DetailField label="Email" value={application.user_email || application.email || application.email_address} icon={Mail} />
                      <DetailField label="Phone" value={application.telephone || application.mobile || application.contact_number || application.phone} icon={Phone} />
                      <DetailField label="Applicant Type" value={application.applicant_type?.replace(/_/g, " ")} />
                      <DetailField label="Loan Type" value={application.loan_type?.replace(/_/g, " ")} />
                      <DetailField label="Applied On" value={formatDate(application.created_at)} icon={Calendar} />
                    </div>
                  </CardContent>
                </Card>

                {/* Business/Individual Specific Info */}
                {application.applicant_type !== "individual" && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Briefcase className="h-5 w-5" />
                        Business Details
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        <DetailField label="Business Name" value={application.business_name || application.institution_name} />
                        <DetailField label="Trading Name" value={application.trading_name} />
                        <DetailField label="Business Type" value={application.business_type} />
                        <DetailField label="Industry Sector" value={application.industry_sector} />
                        <DetailField label="Registration Number" value={application.company_reg_number} />
                        <DetailField label="Date Incorporated" value={application.date_incorporated || application.year_incorporated} />
                        <DetailField label="Years in Business" value={application.years_in_business} />
                        <DetailField label="Number of Employees" value={application.employees_count || application.number_of_employees} icon={Users} />
                        <DetailField label="Website" value={application.website} icon={Globe} />
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Address Information */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <MapPin className="h-5 w-5" />
                      Address Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <DetailField label="Registered Address" value={application.registered_address || application.business_address || application.individual_address} />
                      <DetailField label="Physical Address" value={application.physical_address} />
                      <DetailField label="City" value={application.city} />
                      <DetailField label="State" value={application.state} />
                      <DetailField label="Postal Code" value={application.postal_code} />
                      <DetailField label="Property Ownership" value={application.property_ownership} />
                    </div>
                  </CardContent>
                </Card>

                {/* Loan Request Details */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <DollarSign className="h-5 w-5" />
                      Loan Request
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <DetailField label="Amount Requested" value={formatCurrency(amount)} />
                      <DetailField label="Term" value={`${application.term_months || application.tenure_months || "N/A"} months`} />
                      <DetailField label="Purpose" value={application.purpose || application.purpose_of_loan} />
                      <DetailField label="Security Type" value={application.security_type} />
                      <DetailField label="Security Description" value={application.security_description} />
                      <DetailField label="Own Capital Available" value={application.own_capital_available ? formatCurrency(application.own_capital_available) : null} />
                    </div>
                  </CardContent>
                </Card>

                {/* Guarantor Info (for Individual loans) */}
                {application.applicant_type === "individual" && application.guarantor_name && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Shield className="h-5 w-5" />
                        Guarantor Information
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        <DetailField label="Guarantor Name" value={application.guarantor_name} />
                        <DetailField label="Guarantor ID" value={application.guarantor_id} />
                        <DetailField label="Guarantor Phone" value={application.guarantor_phone} />
                        <DetailField label="Guarantor Address" value={application.guarantor_address} />
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Directors (for Business/Wholesale) */}
                {application.directors && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Users className="h-5 w-5" />
                        Directors / Key Personnel
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-700 whitespace-pre-wrap">{application.directors}</p>
                    </CardContent>
                  </Card>
                )}

                {/* Collateral Items (for Wholesale) */}
                {application.collateral_items?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Shield className="h-5 w-5" />
                        Collateral Items
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {application.collateral_items.map((item, idx) => (
                          <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                            <p className="font-medium">{item.description || item.type}</p>
                            {item.value && <p className="text-sm text-gray-600">Value: {formatCurrency(item.value)}</p>}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              {/* Financials Tab */}
              <TabsContent value="financials" className="space-y-4 mt-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <TrendingUp className="h-5 w-5" />
                      Financial Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <DetailField label="Annual Revenue" value={application.annual_revenue ? formatCurrency(application.annual_revenue) : null} />
                      <DetailField label="Monthly Expenses" value={application.monthly_expenses ? formatCurrency(application.monthly_expenses) : null} />
                      <DetailField label="Existing Debt" value={application.existing_debt ? formatCurrency(application.existing_debt) : null} />
                      <DetailField label="Total Assets" value={application.total_assets ? formatCurrency(application.total_assets) : null} />
                      <DetailField label="Total Liabilities" value={application.total_liabilities ? formatCurrency(application.total_liabilities) : null} />
                      <DetailField label="Capital" value={application.capital ? formatCurrency(application.capital) : null} />
                      <DetailField label="Interest Income" value={application.interest_income ? formatCurrency(application.interest_income) : null} />
                      <DetailField label="Gross Income" value={application.gross_income ? formatCurrency(application.gross_income) : null} />
                      <DetailField label="Net Profit/Loss" value={application.net_profit_loss ? formatCurrency(application.net_profit_loss) : null} />
                    </div>
                  </CardContent>
                </Card>

                {/* Existing Loans */}
                {application.has_existing_loans === "yes" && application.existing_loans?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg">Existing Loans</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {application.existing_loans.map((loan, idx) => (
                          <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                              <div><span className="text-gray-500">Lender:</span> {loan.lender || "N/A"}</div>
                              <div><span className="text-gray-500">Amount:</span> {loan.amount ? formatCurrency(loan.amount) : "N/A"}</div>
                              <div><span className="text-gray-500">Outstanding:</span> {loan.outstanding ? formatCurrency(loan.outstanding) : "N/A"}</div>
                              <div><span className="text-gray-500">Monthly Payment:</span> {loan.monthly_payment ? formatCurrency(loan.monthly_payment) : "N/A"}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Alternative Data */}
                {alternativeData.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <BarChart3 className="h-5 w-5" />
                        Alternative Data Submitted
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {alternativeData.map((data, idx) => (
                          <div key={idx} className="p-3 bg-blue-50 rounded-lg">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant="outline" className="capitalize">{data.data_type?.replace(/_/g, " ")}</Badge>
                              <span className="text-xs text-gray-500">{formatDate(data.created_at)}</span>
                            </div>
                            <p className="text-sm text-gray-700">{data.data_summary}</p>
                            {data.metrics && Object.keys(data.metrics).length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-2">
                                {Object.entries(data.metrics).map(([key, value]) => (
                                  <Badge key={key} variant="secondary" className="text-xs">
                                    {key}: {typeof value === 'number' ? value.toLocaleString() : value}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Business Model Info (for Wholesale) */}
                {(application.products || application.customer_segments) && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg">Business Model</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <DetailField label="Products/Services" value={application.products} />
                        <DetailField label="Customer Segments" value={application.customer_segments} />
                        <DetailField label="Number of Clients" value={application.number_of_clients} />
                        <DetailField label="Geographical Coverage" value={Array.isArray(application.geographical_coverage) ? application.geographical_coverage.join(", ") : application.geographical_coverage} />
                      </div>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              {/* Documents Tab */}
              <TabsContent value="documents" className="space-y-4 mt-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <FileCheck className="h-5 w-5" />
                      Uploaded Documents
                    </CardTitle>
                    <CardDescription>
                      {documents.length > 0 || application.documents_count > 0
                        ? `${documents.length || application.documents_count} document(s) uploaded`
                        : "No documents uploaded yet"}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {documents.length > 0 ? (
                      <div className="space-y-3">
                        {documents.map((doc, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                            <div className="flex items-center gap-3">
                              <div className="h-10 w-10 rounded bg-blue-100 flex items-center justify-center">
                                <FileText className="h-5 w-5 text-blue-600" />
                              </div>
                              <div>
                                <p className="font-medium text-sm">{doc.file_name || doc.category || "Document"}</p>
                                <p className="text-xs text-gray-500 capitalize">
                                  {doc.category?.replace(/_/g, " ")} • {formatDate(doc.uploaded_at || doc.created_at)}
                                  {doc.file_size && ` • ${(doc.file_size / 1024).toFixed(1)} KB`}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => downloadDocument(doc.document_id)}
                                data-testid={`download-doc-${idx}`}
                              >
                                <Download className="h-4 w-4 mr-1" />
                                Download
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : application.documents_meta && Object.keys(application.documents_meta).length > 0 ? (
                      <div className="space-y-3">
                        {Object.entries(application.documents_meta).map(([category, doc], idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                            <div className="flex items-center gap-3">
                              <div className="h-10 w-10 rounded bg-blue-100 flex items-center justify-center">
                                <FileText className="h-5 w-5 text-blue-600" />
                              </div>
                              <div>
                                <p className="font-medium text-sm capitalize">{category.replace(/_/g, " ")}</p>
                                <p className="text-xs text-gray-500">
                                  {doc.file_name || "Uploaded document"}
                                </p>
                              </div>
                            </div>
                            <Badge className="bg-green-100 text-green-700">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Uploaded
                            </Badge>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <FileText className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                        <p className="text-gray-500">No documents have been uploaded for this application.</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* History Tab */}
              <TabsContent value="history" className="space-y-4 mt-4">
                {application.workflow_history?.length > 0 ? (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <History className="h-5 w-5" />
                        Workflow History
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {application.workflow_history.map((entry, idx) => (
                          <div key={idx} className="flex gap-4 pb-4 border-b last:border-0">
                            <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                              <Shield className="h-4 w-4 text-gray-600" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium">{entry.performed_by_name}</span>
                                <Badge variant="outline" className="text-xs">
                                  {entry.performed_by_role?.replace(/_/g, " ")}
                                </Badge>
                              </div>
                              <p className="text-sm text-gray-600 capitalize">
                                {entry.action?.replace(/_/g, " ")}
                              </p>
                              {entry.recommendation && (
                                <p className="text-sm mt-1 p-2 bg-blue-50 rounded">
                                  <strong>Recommendation:</strong> {entry.recommendation}
                                </p>
                              )}
                              {entry.comment && (
                                <p className="text-sm mt-1 p-2 bg-gray-50 rounded">
                                  <strong>Comment:</strong> {entry.comment}
                                </p>
                              )}
                              <p className="text-xs text-gray-400 mt-1">
                                {formatDate(entry.created_at)}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <Card>
                    <CardContent className="py-8 text-center">
                      <History className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                      <p className="text-gray-500">No workflow history yet.</p>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
            </Tabs>
          </div>

          {/* Actions Sidebar */}
          <div className="space-y-6">
            {/* Credit Score / Risk Assessment Card */}
            <Card className="border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-amber-600" />
                  Risk Assessment
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {creditScore ? (
                  <>
                    {/* Score Display */}
                    <div className="text-center py-3">
                      <div className={`text-5xl font-bold ${getRiskColor(creditScore.score)}`}>
                        {creditScore.score}
                      </div>
                      <p className="text-sm text-gray-500 mt-1">out of 100</p>
                      <Badge className={`mt-2 ${getRiskBgColor(creditScore.score)} ${getRiskColor(creditScore.score)}`}>
                        {creditScore.risk_level} RISK
                      </Badge>
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="space-y-1">
                      <div className="flex justify-between text-xs text-gray-500">
                        <span>High Risk</span>
                        <span>Low Risk</span>
                      </div>
                      <Progress value={creditScore.score} className="h-2" />
                    </div>
                    
                    {/* Recommendation */}
                    <div className={`p-3 rounded-lg ${
                      creditScore.recommendation === "APPROVE" ? "bg-green-100" :
                      creditScore.recommendation === "REVIEW" ? "bg-yellow-100" : "bg-red-100"
                    }`}>
                      <p className={`text-sm font-medium ${
                        creditScore.recommendation === "APPROVE" ? "text-green-700" :
                        creditScore.recommendation === "REVIEW" ? "text-yellow-700" : "text-red-700"
                      }`}>
                        System Recommendation: {creditScore.recommendation}
                      </p>
                    </div>
                    
                    {/* Score Factors */}
                    {creditScore.factors && creditScore.factors.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-gray-500 uppercase">Score Factors</p>
                        <div className="space-y-1 max-h-40 overflow-y-auto">
                          {creditScore.factors.map((factor, idx) => (
                            <div key={idx} className="flex items-center justify-between text-xs p-2 bg-white rounded border">
                              <span className="text-gray-700">{factor.factor}</span>
                              <Badge variant="outline" className={
                                factor.impact?.startsWith("+") ? "text-green-600 border-green-300" :
                                factor.impact?.startsWith("-") ? "text-red-600 border-red-300" : "text-gray-600"
                              }>
                                {factor.impact}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* AI Assessment */}
                    {application.ai_assessment && (
                      <div className="space-y-2 pt-2 border-t">
                        <div className="flex items-center gap-2">
                          <Brain className="h-4 w-4 text-purple-600" />
                          <p className="text-xs font-medium text-purple-600 uppercase">AI Assessment</p>
                        </div>
                        <div className="p-2 bg-purple-50 rounded text-xs text-gray-700 max-h-32 overflow-y-auto">
                          {application.ai_assessment}
                        </div>
                        {application.ai_risk_score && (
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-500">AI Risk Score</span>
                            <Badge className={getRiskBgColor(application.ai_risk_score)}>
                              {application.ai_risk_score}/100
                            </Badge>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Recalculate Button */}
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full"
                      onClick={calculateScore}
                      disabled={scoreLoading}
                    >
                      {scoreLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                      Recalculate Score
                    </Button>
                  </>
                ) : (
                  <div className="text-center py-4">
                    <BarChart3 className="h-10 w-10 mx-auto text-gray-300 mb-2" />
                    <p className="text-sm text-gray-500 mb-3">No credit score calculated yet</p>
                    <Button 
                      className="w-full bg-amber-600 hover:bg-amber-700"
                      onClick={calculateScore}
                      disabled={scoreLoading}
                      data-testid="calculate-score-btn"
                    >
                      {scoreLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <BarChart3 className="h-4 w-4 mr-2" />}
                      Calculate Credit Score
                    </Button>
                  </div>
                )}
                
                {/* AI Assessment Button */}
                {!application.ai_assessment && (
                  <Button 
                    variant="outline"
                    className="w-full border-purple-300 text-purple-700 hover:bg-purple-50"
                    onClick={runAIAssessment}
                    disabled={scoreLoading}
                    data-testid="ai-assessment-btn"
                  >
                    {scoreLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Brain className="h-4 w-4 mr-2" />}
                    Run AI Risk Assessment
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Current Recommendation/Decision */}
            {application.recommendation && (
              <Card className="border-indigo-200 bg-indigo-50/50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-indigo-700 flex items-center gap-2">
                    <MessageSquare className="h-4 w-4" />
                    Loan Officer Recommendation
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm">{application.recommendation}</p>
                  {application.risk_notes && (
                    <p className="text-sm text-gray-600 mt-2">
                      <strong>Risk Notes:</strong> {application.risk_notes}
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-2">
                    By {application.recommended_by_name} on {formatDate(application.recommended_at)}
                  </p>
                </CardContent>
              </Card>
            )}

            {application.manager_comment && (
              <Card className="border-blue-200 bg-blue-50/50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-blue-700 flex items-center gap-2">
                    <Shield className="h-4 w-4" />
                    Manager Decision
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Badge className={application.manager_decision === "escalated" ? "bg-orange-100 text-orange-700" : 
                    application.manager_decision === "approved" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}>
                    {application.manager_decision?.toUpperCase()}
                  </Badge>
                  <p className="text-sm mt-2">{application.manager_comment}</p>
                  <p className="text-xs text-gray-500 mt-2">
                    By {application.manager_name} on {formatDate(application.manager_decision_at)}
                  </p>
                </CardContent>
              </Card>
            )}

            {application.executive_comment && (
              <Card className="border-purple-200 bg-purple-50/50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-purple-700 flex items-center gap-2">
                    <Shield className="h-4 w-4" />
                    Executive Decision
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Badge className={application.executive_decision === "approved" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}>
                    {application.executive_decision?.toUpperCase()}
                  </Badge>
                  <p className="text-sm mt-2">{application.executive_comment}</p>
                  <p className="text-xs text-gray-500 mt-2">
                    By {application.executive_name} on {formatDate(application.executive_decision_at)}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Email Notification Status */}
            {(application.status === "approved" || application.status === "rejected") && (
              <Card className="border-emerald-200 bg-emerald-50/50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-emerald-700 flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Applicant Notification
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {notifications.length > 0 ? (
                    <div className="space-y-2">
                      {notifications.map((notif, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm">
                          <Send className="h-3 w-3 text-emerald-600" />
                          <span className="text-emerald-700">Email sent to {notif.recipient}</span>
                        </div>
                      ))}
                      <p className="text-xs text-gray-500 mt-1">
                        {notifications[0] && formatDate(notifications[0].sent_at)}
                      </p>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-sm text-amber-600">
                      <AlertTriangle className="h-3 w-3" />
                      <span>Notification pending (check email config)</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Action Buttons */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {canRecommend && (
                  <Button 
                    className="w-full bg-indigo-600 hover:bg-indigo-700"
                    onClick={() => setShowRecommendDialog(true)}
                    data-testid="add-recommendation-btn"
                  >
                    <MessageSquare className="h-4 w-4 mr-2" />
                    Add Recommendation
                  </Button>
                )}

                {canManagerDecide && (
                  <>
                    {amount <= 5000 ? (
                      <>
                        <Button 
                          className="w-full bg-green-600 hover:bg-green-700"
                          onClick={() => { setPendingDecision("approved"); setShowManagerDialog(true); }}
                          data-testid="approve-btn"
                        >
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Approve (≤$5k)
                        </Button>
                        <Button 
                          variant="destructive"
                          className="w-full"
                          onClick={() => { setPendingDecision("rejected"); setShowManagerDialog(true); }}
                          data-testid="reject-btn"
                        >
                          <XCircle className="h-4 w-4 mr-2" />
                          Reject
                        </Button>
                      </>
                    ) : (
                      <>
                        <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                          <div className="flex items-center gap-2 text-orange-700 mb-1">
                            <AlertTriangle className="h-4 w-4" />
                            <span className="text-sm font-medium">Amount exceeds $5,000</span>
                          </div>
                          <p className="text-xs text-orange-600">Must be escalated to Executive for approval</p>
                        </div>
                        <Button 
                          className="w-full bg-orange-600 hover:bg-orange-700"
                          onClick={() => { setPendingDecision("escalated"); setShowManagerDialog(true); }}
                          data-testid="escalate-btn"
                        >
                          <ArrowUpRight className="h-4 w-4 mr-2" />
                          Escalate to Executive
                        </Button>
                        <Button 
                          variant="destructive"
                          className="w-full"
                          onClick={() => { setPendingDecision("rejected"); setShowManagerDialog(true); }}
                          data-testid="reject-btn"
                        >
                          <XCircle className="h-4 w-4 mr-2" />
                          Reject
                        </Button>
                      </>
                    )}
                  </>
                )}

                {canExecutiveDecide && (
                  <>
                    <Button 
                      className="w-full bg-green-600 hover:bg-green-700"
                      onClick={() => { setPendingDecision("approved"); setShowExecutiveDialog(true); }}
                      data-testid="exec-approve-btn"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Approve
                    </Button>
                    <Button 
                      variant="destructive"
                      className="w-full"
                      onClick={() => { setPendingDecision("rejected"); setShowExecutiveDialog(true); }}
                      data-testid="exec-reject-btn"
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      Reject
                    </Button>
                  </>
                )}

                {!canRecommend && !canManagerDecide && !canExecutiveDecide && (
                  <p className="text-sm text-gray-500 text-center py-4">
                    No actions available for your role at this stage.
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Recommendation Dialog */}
      <Dialog open={showRecommendDialog} onOpenChange={setShowRecommendDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Recommendation</DialogTitle>
            <DialogDescription>
              Provide your recommendation and risk assessment for this loan application.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="recommendation">Recommendation *</Label>
              <Textarea
                id="recommendation"
                placeholder="Enter your recommendation for this application..."
                rows={4}
                value={recommendation}
                onChange={(e) => setRecommendation(e.target.value)}
                data-testid="recommendation-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="riskNotes">Risk Notes (Optional)</Label>
              <Textarea
                id="riskNotes"
                placeholder="Any risk factors or concerns..."
                rows={3}
                value={riskNotes}
                onChange={(e) => setRiskNotes(e.target.value)}
                data-testid="risk-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRecommendDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleRecommendation} 
              disabled={actionLoading}
              data-testid="submit-recommendation-btn"
            >
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit Recommendation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manager Decision Dialog */}
      <Dialog open={showManagerDialog} onOpenChange={setShowManagerDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {pendingDecision === "approved" && "Approve Application"}
              {pendingDecision === "rejected" && "Reject Application"}
              {pendingDecision === "escalated" && "Escalate to Executive"}
            </DialogTitle>
            <DialogDescription>
              {pendingDecision === "escalated" 
                ? "This application will be sent to an Executive for final approval."
                : `Confirm your decision to ${pendingDecision} this application.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="managerComment">Comment *</Label>
              <Textarea
                id="managerComment"
                placeholder={pendingDecision === "escalated" 
                  ? "Explain why this needs executive approval..."
                  : "Enter your comments..."}
                rows={4}
                value={managerComment}
                onChange={(e) => setManagerComment(e.target.value)}
                data-testid="manager-comment-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowManagerDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => handleManagerDecision(pendingDecision)}
              disabled={actionLoading}
              className={
                pendingDecision === "approved" ? "bg-green-600 hover:bg-green-700" :
                pendingDecision === "escalated" ? "bg-orange-600 hover:bg-orange-700" :
                "bg-red-600 hover:bg-red-700"
              }
              data-testid="confirm-manager-decision-btn"
            >
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 
                pendingDecision === "approved" ? "Approve" :
                pendingDecision === "escalated" ? "Escalate" : "Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Executive Decision Dialog */}
      <Dialog open={showExecutiveDialog} onOpenChange={setShowExecutiveDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {pendingDecision === "approved" ? "Approve Application" : "Reject Application"}
            </DialogTitle>
            <DialogDescription>
              As an Executive, your decision is final.
            </DialogDescription>
          </DialogHeader>
          {application?.manager_comment && (
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-sm font-medium text-blue-700">Manager's Escalation Comment:</p>
              <p className="text-sm text-blue-600 mt-1">{application.manager_comment}</p>
            </div>
          )}
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="executiveComment">Comment *</Label>
              <Textarea
                id="executiveComment"
                placeholder="Enter your decision comments..."
                rows={4}
                value={executiveComment}
                onChange={(e) => setExecutiveComment(e.target.value)}
                data-testid="executive-comment-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExecutiveDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => handleExecutiveDecision(pendingDecision)}
              disabled={actionLoading}
              className={pendingDecision === "approved" ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"}
              data-testid="confirm-executive-decision-btn"
            >
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 
                pendingDecision === "approved" ? "Approve" : "Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </BackOfficeLayout>
  );
};

export default BackOfficeReview;
